import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonBadge,
    IonLabel,
    IonIcon,
    IonButton,
    IonAlert,
    IonToast,
    IonSpinner,
    IonText,
    IonChip,
    IonRefresherContent,
    IonInput,
    IonGrid,
    IonRow,
    IonCol,
} from '@ionic/react';
import {
    fingerPrint,
    trash,
    add,
    checkmarkCircle,
    alertCircle,
    phonePortrait,
    desktop,
    laptop,
    pencil,
    closeOutline,
    time,
    addOutline,
} from 'ionicons/icons';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import { getDeviceInfo } from '@rsmaids/shared/utils/deviceUtils';
import BasePageWithHeader, { TitleCard } from '../BasePageWithHeader';
import PositionedRefresher from '../PositionedRefresher';
import styles from './PasskeyManagement.module.scss';
import { useTranslation } from 'react-i18next';

interface Passkey {
    id: string;
    deviceType: string;
    os: string;
    browser?: string;
    createdAt: Date;
    lastUsedAt?: Date;
    nickname?: string;
    counter: number;
}

interface PasskeyManagementProps {
    onClose?: () => void;
    onPasskeysChange?: (count: number) => void;
}

export const PasskeyManagement: React.FC<PasskeyManagementProps> = ({ onClose, onPasskeysChange }) => {
  const { t } = useTranslation();
    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const contentRef = useRef<HTMLIonGridElement>(null);

    const [passkeys, setPasskeys] = useState<Passkey[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [selectedPasskey, setSelectedPasskey] = useState<Passkey | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [editingPasskeyId, setEditingPasskeyId] = useState<string | null>(null);
    const [nicknameInput, setNicknameInput] = useState('');
    const [addingPasskey, setAddingPasskey] = useState(false);

    const loadPasskeys = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            setError(null);
            const result = await rsAuth.listUserPasskeys();
            if (result.success && result.passkeys) {
                setPasskeys(result.passkeys.map((pk) => ({
                    ...pk,
                    createdAt: new Date(pk.createdAt),
                    lastUsedAt: pk.lastUsedAt ? new Date(pk.lastUsedAt) : undefined,
                })));
            } else {
                setError(result.error || t('components.PasskeyManagement.PasskeyManagement.failedToLoadPasskeys'));
            }
        } catch (err) {
            setError(t('components.PasskeyManagement.PasskeyManagement.anErrorOccurredWhileLoadingPasskeys'));
            console.error(err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        Promise.resolve().then(() => loadPasskeys());
    }, [loadPasskeys]);

    useEffect(() => {
        if (onPasskeysChange) {
            onPasskeysChange(passkeys.length);
        }
    }, [passkeys, onPasskeysChange]);

    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(contentRef, 66, applyProgress);

    const handleRefresh = async (event: CustomEvent) => {
        await loadPasskeys(true);
        event.detail.complete();
    };

    const handleDeleteClick = (passkey: Passkey) => {
        setSelectedPasskey(passkey);
        setShowDeleteAlert(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedPasskey) return;
        try {
            const result = await rsAuth.revokePasskey(selectedPasskey.id);
            if (result.success) {
                setPasskeys(passkeys.filter(pk => pk.id !== selectedPasskey.id));
                setToastMessage(t('components.PasskeyManagement.PasskeyManagement.passkeyDeletedSuccessfully'));
                setShowToast(true);
            } else {
                setError(result.error || t('components.PasskeyManagement.PasskeyManagement.failedToDeletePasskey'));
            }
        } catch (err) {
            setError(t('components.PasskeyManagement.PasskeyManagement.anErrorOccurredWhileDeletingThe'));
            console.error(err);
        }
    };

    const handleDeleteDismiss = () => {
        setShowDeleteAlert(false);
        setSelectedPasskey(null);
    };

    const handleEditNickname = (passkey: Passkey) => {
        setEditingPasskeyId(passkey.id);
        setNicknameInput(passkey.nickname || '');
    };

    const handleCancelNicknameEdit = () => {
        setEditingPasskeyId(null);
        setNicknameInput('');
    };

    const handleSaveNickname = async () => {
        if (!editingPasskeyId) return;
        try {
            const result = await rsAuth.updatePasskeyNickname(editingPasskeyId, nicknameInput.trim());
            if (result.success) {
                setPasskeys(passkeys.map(pk =>
                    pk.id === editingPasskeyId ? { ...pk, nickname: nicknameInput.trim() } : pk
                ));
                setToastMessage(t('components.PasskeyManagement.PasskeyManagement.nicknameUpdatedSuccessfully'));
                setShowToast(true);
            } else {
                setError(result.error || t('components.PasskeyManagement.PasskeyManagement.failedToUpdateNickname'));
            }
        } catch (err) {
            setError(t('components.PasskeyManagement.PasskeyManagement.anErrorOccurredWhileUpdatingThe'));
            console.error(err);
        } finally {
            handleCancelNicknameEdit();
        }
    };

    const handleAddPasskey = async () => {
        try {
            setAddingPasskey(true);
            const deviceInfo = await getDeviceInfo();
            const result = await rsAuth.registerWithPasskey(undefined, { ...deviceInfo });
            if (result.success) {
                await loadPasskeys();
                setToastMessage(t('components.PasskeyManagement.PasskeyManagement.newPasskeyAddedSuccessfully'));
                setShowToast(true);
            } else {
                setError(result.error || t('components.PasskeyManagement.PasskeyManagement.failedToAddPasskey'));
            }
        } catch (err) {
            setError(t('components.PasskeyManagement.PasskeyManagement.anErrorOccurredWhileAddingThe'));
            console.error(err);
        } finally {
            setAddingPasskey(false);
        }
    };

    const getDeviceIcon = (deviceType: string) => {
        switch (deviceType.toLowerCase()) {
            case 'mobile': return phonePortrait;
            case 'desktop': return desktop;
            case 'tablet': return laptop;
            default: return fingerPrint;
        }
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const getTimeSince = (date: Date) => {
        const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
        if (seconds < 60) return 'just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
        return formatDate(date);
    };

    const pageContent = (
        <BasePageWithHeader
            ref={baseHeaderRef}
            title={t('components.PasskeyManagement.PasskeyManagement.passkeys')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
                onClose && (
                    <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
                        <IonIcon icon={closeOutline} />
                    </IonButton>
                )
            }
            endButtons={
                <IonButton
                    shape="round"
                    className={styles.addButton}
                    onClick={handleAddPasskey}
                    disabled={addingPasskey}
                >
                    {addingPasskey ? (
                        <IonSpinner name="bubbles" color="primary" />
                    ) : (
                        <IonIcon icon={addOutline} />
                    )}
                </IonButton>
            }
        >
            <IonContent scrollY={false} className={styles.content}>
                <PositionedRefresher
                    titleRef={titleCardAnimationRef}
                    contentRef={contentRef}
                    offset={46}
                    anchor="bottom"
                    pullMin={120}
                    pullMax={200}
                    pullFactor={0.6}
                    onIonRefresh={handleRefresh}
                    style={{
                        '--ion-color-step-250': 'var(--ion-background-color)',
                        '--ion-color-step-200': 'var(--ion-background-color)',
                        '--ion-background-color': 'transparent',
                    } as React.CSSProperties}
                >
                    <IonRefresherContent
                        pullingIcon="bubbles"
                        refreshingSpinner="bubbles"
                    />
                </PositionedRefresher>

                <IonGrid
                    ref={contentRef}
                    className={styles.container}
                    onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
                >
                    <TitleCard
                        ref={titleCardAnimationRef}
                        title={t('components.PasskeyManagement.PasskeyManagement.passkeys')}
                        cardClassName={styles.titleCard}
                        titleContainerClassName={styles.titleContainer}
                        titleClassName={styles.titleText}
                    />

                    <IonCard className={styles.summaryCard}>
                        <IonCardContent className={styles.summaryCardBody}>
                            <IonRow className={styles.summaryRow}>
                                <IonCol size="auto" className={styles.iconCircle}>
                                    <IonIcon icon={fingerPrint} className={styles.titleIcon} />
                                </IonCol>
                                <IonCol className={styles.summaryCopy}>
                                    <h3 className={styles.titleDescriptionHeading}>{t('components.PasskeyManagement.PasskeyManagement.passwordlessSignIn')}</h3>
                                    <p className={styles.titleDescription}>{t('components.PasskeyManagement.PasskeyManagement.passkeysUseYourDeviceSFingerprint')}
                                    </p>
                                </IonCol>
                            </IonRow>
                        </IonCardContent>
                    </IonCard>

                    {/* Loading / Error / Empty states */}
                    {loading ? (
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent>
                                <IonRow className={styles.loadingContainer}>
                                    <IonSpinner name="bubbles" color="primary" />
                                    <IonText color="medium"><p>{t('components.PasskeyManagement.PasskeyManagement.loadingYourPasskeys')}</p></IonText>
                                </IonRow>
                            </IonCardContent>
                        </IonCard>
                    ) : error ? (
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent className={styles.errorContainer}>
                                    <IonIcon icon={alertCircle} className={styles.errorIcon} />
                                    <IonText className={styles.errorMessage}>{error}</IonText>
                                    <IonButton
                                        onClick={() => loadPasskeys()}
                                        fill="clear"
                                        size="small"
                                        className={styles.retryButton}
                                    >{t('components.PasskeyManagement.PasskeyManagement.tryAgain')}
                                    </IonButton>
                            </IonCardContent>
                        </IonCard>
                    ) : passkeys.length === 0 ? (
                        <IonCard className={styles.sectionCard}>
                            <IonCardContent>
                                <IonRow className={styles.emptyContainer}>
                                    <IonIcon icon={fingerPrint} className={styles.emptyIcon} />
                                    <IonText color="medium">
                                        <h3>{t('components.PasskeyManagement.PasskeyManagement.noPasskeysYet')}</h3>
                                        <p>{t('components.PasskeyManagement.PasskeyManagement.addYourFirstPasskeyToEnable')}</p>
                                    </IonText>
                                    <IonButton onClick={handleAddPasskey} disabled={addingPasskey} className="ion-margin-top">
                                        <IonIcon icon={add} slot="start" />{t('components.PasskeyManagement.PasskeyManagement.addYourFirstPasskey')}
                                    </IonButton>
                                </IonRow>
                            </IonCardContent>
                        </IonCard>
                    ) : (
                        <>
                            <IonRow className={styles.passkeyHeader}>
                                <h3 className={styles.passkeyHeaderTitle}>{t('components.PasskeyManagement.PasskeyManagement.yourPasskeys')}
                                    <IonBadge className={styles.countBadge}>{passkeys.length}</IonBadge>
                                </h3>
                            </IonRow>

                            {passkeys.map((passkey) => {
                                const isEditingNickname = editingPasskeyId === passkey.id;

                                return (
                                <IonCard key={passkey.id} className={styles.sectionCard}>
                                    <IonCardContent>
                                        <IonGrid className={styles.passkeyGrid}>
                                            <IonRow className="ion-align-items-center">
                                                <IonCol className={styles.contentCol}>
                                                    <IonRow className={`ion-no-padding ion-align-items-center ${styles.nameRow}`}>
                                                        <IonCol className={styles.nameCol}>
                                                            <IonRow className={`ion-no-padding ion-align-items-center ${styles.nameWithIcon}`}>
                                                                <IonIcon icon={getDeviceIcon(passkey.deviceType)} className={`${styles.deviceIcon} ${styles.deviceIconInline}`} />
                                                                <h2 className={styles.passkeyName}>{passkey.nickname || passkey.deviceType}</h2>
                                                            </IonRow>
                                                        </IonCol>
                                                        {!isEditingNickname && (
                                                            <IonCol size="auto" className={styles.buttonsCol}>
                                                                <IonButton fill="clear" shape="round" onClick={() => handleEditNickname(passkey)} className={styles.iconButton}>
                                                                    <IonIcon icon={pencil} slot="icon-only" />
                                                                </IonButton>
                                                                <IonButton fill="clear" color="danger" shape="round" onClick={() => handleDeleteClick(passkey)} disabled={passkeys.length === 1} className={styles.iconButton}>
                                                                    <IonIcon icon={trash} slot="icon-only" />
                                                                </IonButton>
                                                            </IonCol>
                                                        )}
                                                    </IonRow>
                                                    <p className={styles.passkeyDetails}>{passkey.browser && `${passkey.browser} • `}{passkey.os}</p>
                                                    <IonRow className={`ion-no-padding ion-no-margin ${styles.passkeyMeta}`}>
                                                        <IonChip className={styles.metaChip} outline>
                                                            <IonIcon icon={time} />
                                                            <IonLabel>{t('components.PasskeyManagement.PasskeyManagement.created')} {getTimeSince(passkey.createdAt)}</IonLabel>
                                                        </IonChip>
                                                        {passkey.lastUsedAt && (
                                                            <IonChip className={styles.metaChip} outline>
                                                                <IonIcon icon={checkmarkCircle} />
                                                                <IonLabel>{t('components.PasskeyManagement.PasskeyManagement.lastUsed')} {getTimeSince(passkey.lastUsedAt)}</IonLabel>
                                                            </IonChip>
                                                        )}
                                                    </IonRow>
                                                    {isEditingNickname && (
                                                        <IonRow className={styles.inlineEditorBlock}>
                                                            <IonCol className={styles.inlineEditor}>
                                                                <IonRow className={styles.inlineFieldGroup}>
                                                                    <IonLabel className={styles.inlineFieldLabel}>{t('components.PasskeyManagement.PasskeyManagement.nickname')}</IonLabel>
                                                                    <IonInput
                                                                        value={nicknameInput}
                                                                        onIonInput={(e) => setNicknameInput(e.detail.value || '')}
                                                                        placeholder={t('components.PasskeyManagement.PasskeyManagement.eGMyIphoneWorkLaptop')}
                                                                        maxlength={50}
                                                                        className={styles.inlineNicknameInput}
                                                                    />
                                                                    <IonText className={styles.inlineCounter}>{nicknameInput.length} / 50</IonText>
                                                                </IonRow>
                                                                <IonRow className={styles.inlineEditActions}>
                                                                    <IonButton
                                                                        fill="solid"
                                                                        onClick={handleSaveNickname}
                                                                        disabled={!nicknameInput.trim()}
                                                                        className={styles.inlineSaveButton}
                                                                    >{t('components.PasskeyManagement.PasskeyManagement.save')}
                                                                    </IonButton>
                                                                    <IonButton
                                                                        fill="clear"
                                                                        shape="round"
                                                                        onClick={handleCancelNicknameEdit}
                                                                        className={styles.inlineCancelButton}
                                                                    >{t('components.PasskeyManagement.PasskeyManagement.cancel')}
                                                                    </IonButton>
                                                                </IonRow>
                                                            </IonCol>
                                                        </IonRow>
                                                    )}
                                                </IonCol>
                                            </IonRow>
                                        </IonGrid>
                                    </IonCardContent>
                                </IonCard>
                                );
                            })}

                            {passkeys.length === 1 && (
                                <IonText className={styles.warningText}>
                                    <p>
                                        <IonIcon icon={alertCircle} />{t('components.PasskeyManagement.PasskeyManagement.youNeedAtLeastOnePasskey')}
                                    </p>
                                </IonText>
                            )}
                        </>
                    )}

                    <IonRow className={styles.scrollSpacer} />
                </IonGrid>
            </IonContent>

            {/* Delete Confirmation Alert */}
            <IonAlert
                isOpen={showDeleteAlert}
                onDidDismiss={handleDeleteDismiss}
                header={t('components.PasskeyManagement.PasskeyManagement.deletePasskey')}
                message={t('components.PasskeyManagement.PasskeyManagement.areYouSureYouWantTo', { v1: selectedPasskey?.nickname ||
                    `${selectedPasskey?.deviceType} - ${selectedPasskey?.os}` })}
                cssClass="logout-alert"
                buttons={[
                    {
                        text: t('components.PasskeyManagement.PasskeyManagement.cancel'),
                        role: 'cancel',
                        handler: handleDeleteDismiss,
                    },
                    {
                        text: t('components.PasskeyManagement.PasskeyManagement.delete'),
                        role: 'destructive',
                        handler: handleDeleteConfirm,
                    },
                ]}
            />
            <IonToast
                isOpen={showToast}
                onDidDismiss={() => setShowToast(false)}
                message={toastMessage}
                duration={3000}
                position="bottom"
                color="success"
            />
        </BasePageWithHeader>
    );

    return onClose
        ? <div className={styles.modalWrapper}>{pageContent}</div>
        : <IonPage className={styles.page}>{pageContent}</IonPage>;
};
